<template>
  <div class="login-component">
    <div class="form-group">
      <input 
        type="text" 
        v-model="phone" 
        class="form-input" 
        :placeholder="phonePlaceholder"
        maxlength="11"
        @input="checkPhoneValid"
        @focus="clearPhonePlaceholder"
        @blur="resetPhonePlaceholder"
      />
    </div>
    
    <div class="verification-code">
      <div class="form-group code-input-container">
        <input 
          type="text" 
          v-model="verificationCode" 
          class="form-input code-input" 
          placeholder="请输入验证码"
          maxlength="6"
        />
      </div>
      <button 
        @click="getCode" 
        class="get-code-btn" 
        :disabled="isGettingCode || !isPhoneValid"
        :class="{ 'btn-disabled': isGettingCode || !isPhoneValid }"
      >
        {{ codeButtonText }}
      </button>
    </div>
    
    <button 
      @click="handleLogin" 
      class="login-btn"
      :disabled="isLoggingIn"
      :class="{ 'btn-disabled': isLoggingIn }"
    >
      {{ isLoggingIn ? '登录中...' : '登录' }}
    </button>
    
    <div class="login-footer">
      <p>如果没有账号会自动注册新账号</p>
      <p>如果想注销账号请联系系统管理员</p>
    </div>
    
    <!-- 全局提示 -->
    <div v-if="globalMessage.show" class="global-message" :class="globalMessage.type">
      {{ globalMessage.text }}
    </div>
  </div>
</template>

<script>
import { login, getVerificationCode } from '../api';

export default {
  name: 'LoginForm',
  data() {
    return {
      phone: '',
      verificationCode: '',
      isGettingCode: false,
      isLoggingIn: false,
      countdown: 0,
      timer: null,
      phonePlaceholder: '请输入手机号',
      isPhoneValid: false,
      globalMessage: {
        show: false,
        text: '',
        type: 'info'
      }
    }
  },
  computed: {
    codeButtonText() {
      return this.countdown > 0 ? `${this.countdown}s` : '获取';
    }
  },
  methods: {
    // 检查手机号是否有效
    checkPhoneValid() {
      const phoneRegex = /^1[3456789]\d{9}$/;
      this.isPhoneValid = phoneRegex.test(this.phone);
    },
    
    // 清除手机号占位符
    clearPhonePlaceholder() {
      this.phonePlaceholder = '';
    },
    
    // 重置手机号占位符
    resetPhonePlaceholder() {
      if (!this.phone) {
        this.phonePlaceholder = '请输入手机号';
      }
    },
    
    // 验证手机号
    validatePhone() {
      const phoneRegex = /^1[3456789]\d{9}$/;
      
      if (!this.phone) {
        return { valid: false, message: '请输入手机号' };
      } else if (!phoneRegex.test(this.phone)) {
        return { valid: false, message: '请输入正确的手机号码' };
      }
      
      return { valid: true };
    },
    
    // 显示全局消息
    showMessage(text, type = 'info', duration = 3000) {
      this.globalMessage = {
        show: true,
        text,
        type
      };
      
      setTimeout(() => {
        this.globalMessage.show = false;
      }, duration);
    },
    
    // 获取验证码
    async getCode() {
      // 验证手机号
      const phoneValidation = this.validatePhone();
      if (!phoneValidation.valid) {
        this.showMessage(phoneValidation.message, 'error');
        return;
      }
      
      if (this.isGettingCode) return;
      
      this.isGettingCode = true;
      
      try {
        // 调用获取验证码API
        const res = await getVerificationCode(this.phone);
        
        if (res.rc) {
          this.showMessage('验证码发送成功，请注意查收', 'success');
          // this.showMessage(res.msg, 'success');
          
          // 开始倒计时
          this.countdown = 60;
          this.timer = setInterval(() => {
            if (this.countdown > 0) {
              this.countdown--;
            } else {
              clearInterval(this.timer);
              this.isGettingCode = false;
            }
          }, 1000);
        } else {
          this.showMessage(res.msg || '获取验证码失败', 'error');
          this.isGettingCode = false;
        }
      } catch (error) {
        console.error('获取验证码失败', error);
        this.showMessage('获取验证码失败，请稍后重试', 'error');
        this.isGettingCode = false;
      }
    },
    
    // 处理登录
    async handleLogin() {
      // 验证手机号
      const phoneValidation = this.validatePhone();
      if (!phoneValidation.valid) {
        this.showMessage(phoneValidation.message, 'error');
        return;
      }
      
      // 验证验证码
      if (!this.verificationCode) {
        this.showMessage('请输入验证码', 'error');
        return;
      }
      
      this.isLoggingIn = true;
      
      try {
        // 调用登录API
        const res = await login(this.phone, this.verificationCode);
        
        if (res.rc === 1 && res.data) {
          // 保存token到localStorage
          const { accessToken, refreshToken, expire, roles } = res.data;
          localStorage.setItem('token', accessToken);
          localStorage.setItem('refreshToken', refreshToken);
          localStorage.setItem('expireAt', String(Math.floor(Date.now() / 1000 + expire)));
          
          if (roles && roles.length > 0) {
            localStorage.setItem('roles', JSON.stringify(roles));
          }
          
          this.showMessage('登录成功', 'success');
          
          // 通知父组件登录成功
          setTimeout(() => {
            this.$emit('login-success', res.data);
          }, 1000);
        } else {
          this.showMessage(res.msg || '登录失败', 'error');
        }
      } catch (error) {
        console.error('登录失败', error);
        this.showMessage('登录失败，请检查手机号和验证码是否正确', 'error');
      } finally {
        this.isLoggingIn = false;
      }
    }
  },
  // 组件销毁时清除定时器
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }
}
</script>

<style scoped>
.login-component {
  position: relative;
}

.form-group {
  margin-bottom: 15px;
  position: relative;
}

.form-input {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 14px;
}

.form-input:focus {
  border-color: #7b4397;
  outline: none;
}

.verification-code {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}

.code-input-container {
  flex: 1;
  margin-bottom: 0;
}

.code-input {
  margin-bottom: 0;
}

.get-code-btn {
  min-width: 80px;
  padding: 0 10px;
  height: 42px;
  background: linear-gradient(90.00deg, rgb(150, 33, 254),rgb(26, 123, 238) 100%);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.btn-disabled {
  background: #cccccc !important;
  cursor: not-allowed;
}

.login-btn {
  width: 100%;
  padding: 12px 0;
  background: linear-gradient(90.00deg, rgb(150, 33, 254),rgb(26, 123, 238) 100%);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 15px;
  transition: all 0.3s;
}

.login-footer {
  text-align: left;
  color: #999;
  font-size: 12px;
  line-height: 1.5;
}

.login-footer p {
  margin: 5px 0;
}

.global-message {
  position: absolute;
  top: -50px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  z-index: 9999;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
  white-space: nowrap;
}

.info {
  background-color: #f4f4f5;
  color: #909399;
}

.success {
  background-color: #f0f9eb;
  color: #67c23a;
}

.warning {
  background-color: #fdf6ec;
  color: #e6a23c;
}

.error {
  background-color: #fef0f0;
  color: #f56c6c;
}
</style> 